// NATIVE
import React, { useState } from 'react';


// INSTALLED
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, alpha } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useDispatch } from 'react-redux';


// CUSTOM
import { theme } from '../commons/theme';
import { CopyRight } from '../components';
import { startLoginWithEmailPassword, startRecoverPassword } from '../store/auth';
import { ILoginWithEmailPassword } from '../commons/interfaces';
import { SnackAlert } from '../components/SnackAlert';
import QiEmailField from '../components/formFields/QiEmailField';





export const LoginPage = () => {


  const dispatch = useDispatch();

  const [isRecovering, setIsRecovering] = useState(false);
  const [alert, setAlert] = useState({ open: false, msg: '', status: 'success' });

  const loginSchema = Yup.object().shape({
    email: Yup.string().email('Email inválido').required('El email es obligatorio'),
    password: Yup.string().min(5, 'Mínimo 5 caracteres').required('La contraseña es obligatoria')
  });

  const recoverSchema = Yup.object().shape({
    email: Yup.string().email('Email inválido').required('El email es obligatorio')
  });

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    if (isRecovering) {
      const response = await dispatch(startRecoverPassword(values.email));
      if (response && response.ok) {
        setAlert({ open: true, msg: 'Se ha enviado un enlace de recuperación a tu correo', status: 'success' });
        setIsRecovering(false);
      } else {
        setAlert({ open: true, msg: response?.message || 'Error al enviar el correo de recuperación', status: 'error' });
      }
    } else {
      const data_login: ILoginWithEmailPassword = {
        email: values.email,
        password: values.password,
        app: 'qihotels'
      }
      const response = await dispatch(startLoginWithEmailPassword(data_login));
      if (response && !response.ok) {
        setAlert({ open: true, msg: response.message || 'Credenciales incorrectas', status: 'error' });
      }
    }
    setSubmitting(false);
  }

  

  return (
      <ThemeProvider theme={theme}>
      <Grid container component="main" sx={{ height: '100vh' }}>
        <CssBaseline />
        <Grid
          size={{ sm:4, md:7, xs: false }}
          sx={{
            backgroundImage: 'url(/qiimagen.png)',
            backgroundRepeat: 'no-repeat',
            backgroundColor: (t) =>
              t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
            backgroundSize: '100% 100%',
            backgroundPosition: 'center',
          }}
        />
        <Grid 
          size={{ xs: 12, sm: 8, md: 5 }}  
          component={Paper} 
          elevation={6} 
          square 
          sx={{ 
            height: '100vh', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            component="img"
            src="/qiimagen-mobil.png"
            sx={{
              display: { xs: 'block', sm: 'none' },
              width: '100%',
              height: '35vh', 
              objectFit: 'fill',
            }}
          />
          
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              px: 4,
              py: { xs: 2, sm: 8 },
              overflowY: 'auto'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ m: 0.5, bgcolor: isRecovering ? 'primary.main' : 'secondary.main', width: 32, height: 32 }}>
                    {isRecovering ? <EmailIcon sx={{ fontSize: 18 }} /> : <LockOutlinedIcon sx={{ fontSize: 18 }} />}
                </Avatar>
                <Typography component="h1" variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center' }}>
                    {isRecovering ? 'Recuperar contraseña' : 'Ingresa tus datos de acceso'}
                </Typography>
                {isRecovering && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', px: 2, mt: 1 }}>
                        Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
                    </Typography>
                )}
            </Box>

            <Formik
              initialValues={{ email: '', password: '' }}
              validationSchema={isRecovering ? recoverSchema : loginSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ isSubmitting, resetForm, values, errors, touched, handleChange, handleBlur }) => (
                <Form autoComplete="none">
                  {/* Campos falsos invisibles para engañar al gestor de contraseñas */}
                  <input type="text" name="fake_user_name" style={{ display: 'none' }} tabIndex={-1} autoComplete="none" />
                  <input type="password" name="fake_password_field" style={{ display: 'none' }} tabIndex={-1} autoComplete="none" />

                  <Box sx={{ mt: 1, width: '100%' }}>
                    {isRecovering ? (
                      <QiEmailField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email de recuperación"
                        name="email"
                        color='primary'
                        variant="outlined"
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}
                      />
                    ) : (
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email"
                        name="email"
                        autoComplete="email"
                        color='secondary'
                        variant="outlined"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.secondary.main, 0.05)
                          }
                        }}
                      />
                    )}

                    {!isRecovering && (
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type="password"
                        id="password"
                        color='secondary'
                        variant="outlined"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.secondary.main, 0.05)
                          }
                        }}
                      />
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      sx={{ 
                        mt: 4, 
                        mb: 2, 
                        py: 1.5, 
                        borderRadius: 2, 
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        fontSize: '0.9rem'
                      }}
                    >
                      {isSubmitting ? 'Procesando...' : (isRecovering ? 'Enviar enlace' : 'Iniciar sesión')}
                    </Button>

                    <Grid container justifyContent="center">
                      <Grid>
                        <Button 
                          variant="text" 
                          color="primary" 
                          size="small"
                          onClick={() => {
                            setIsRecovering(!isRecovering);
                            resetForm();
                          }}
                          startIcon={isRecovering ? <ArrowBackIcon /> : null}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          {isRecovering ? 'Volver al inicio de sesión' : '¿Olvidaste tu contraseña?'}
                        </Button>
                      </Grid>
                    </Grid>

                    <CopyRight sx={{ mt: 5 }} />
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Grid>
      </Grid>
      <SnackAlert 
          open={alert.open} 
          onclick={(val: boolean) => setAlert({ ...alert, open: val })} 
          text={alert.msg} 
          severity={alert.status} 
      />
    </ThemeProvider>
  )
}